<script lang="ts" setup>
import { ButtonStyle } from '~/static/enums/components/button'
import AButton from '~/components/atoms/buttons/Button.vue'
import AIconLogo from '~/components/atoms/icons/Logo.vue'
import RModalSupport from '~/components/organs/modals/Support.vue'
import RModalInstruction from '~/components/organs/modals/Instruction.vue'
import RModalSignIn from '~/components/organs/modals/SignIn.vue'
import MDropdownMenu from '~/components/molecules/DropdownMenu.vue'
import RModalNameChange from '~/components/organs/modals/NameChange.vue'
import RModalDeleteFile from '~/components/organs/modals/DeleteFile.vue'
import RModalDownloadNotAvailable from '~/components/organs/modals/DownloadNotAvailable.vue'
import MAlert from '~/components/molecules/Alert.vue'
import RModalContainer from '~/components/organs/ModalContainer.vue'
import { CardType } from '~/static/enums/card-type'
import { useEditorStore } from '~/store/editor'
import { storeToRefs } from 'pinia'
import { useAuth } from '~/store/auth'
import { SubscriptionType } from '~/static/enums/user/subscription-type'
import { openModal, closeModal } from 'jenesius-vue-modal'
import { Dropdown as FDropdown, VTooltip as vTooltip } from 'floating-vue'
import { PopupType } from '~/static/enums/popup'
import RModalMobileNav from '~/components/organs/modals/MobileNav.vue'
import { useRemovalBackgroundStore } from '~/store/removal-background'
import { BackgroundRemovalStatus } from '~/static/enums/image/background-removal-status'

interface Props {
  isEditor: boolean
}

withDefaults(defineProps<Props>(), {
  isEditor: false,
})

const backgroundRemovalStore = useRemovalBackgroundStore()
const { backgroundRemovalStatus, imageUrl } = storeToRefs(backgroundRemovalStore)

const isInfograficState = computed(() => {
  return backgroundRemovalStatus.value === BackgroundRemovalStatus.Pending && imageUrl.value !== null
})

const isTemplate = computed<boolean>(() => {
  return useRoute().name === 'template-id'
})

const authStore = useAuth()
const { isAuth, isAdmin, user } = storeToRefs(authStore)

const { $listen, $off, $event } = useNuxtApp()

const isPaid = computed(() => {
  return user.value && user.value.subscription_type !== SubscriptionType.Free
})

const store = useEditorStore()
const {
  id,
  name,
  nameForChanging,
  nameError,
  type,
  getCurrentStep,
} = storeToRefs(store)

const popupType = ref<PopupType | false>(false)
const popupText = ref('')

onMounted(() => {
  $listen('editor:saved', showSavedTooltip)
  $listen('popup:show', showPopup)
})
onBeforeUnmount(() => {
  $off('editor:saved', showSavedTooltip)
  $off('popup:show', showPopup)
})

const showPopup = (payload: { type?: PopupType, text: string, closeAfter?: number }) => {
  popupType.value = payload.type ?? PopupType.Warning
  popupText.value = payload.text
  if (payload.closeAfter) {
    setTimeout(closePopup, payload.closeAfter * 1000)
  }
}

const closePopup = () => {
  popupType.value = false
}

const saved = ref(false)
const showSavedTooltip = () => {
  saved.value = true
  setTimeout(() => {
    saved.value = false
  }, 3000)
}

const saveActualData = () => {
  $event('editor:save')
}

const deleteFile = () => {
  store.delete()
}

const openChangeNameModal = async () => {
  const modal = await openModal(RModalNameChange, {
    name: nameForChanging,
    error: nameError,
  })
  modal.on('change-name', changeNameForChanging)
  modal.on('save', changeName)
  modal.on('cancel', cancelChangingName)
}
const changeNameForChanging = (value: string) => {
  nameForChanging.value = value
}
const changeName = async () => {
  if (await store.changeName()) {
    closeModal()
  }
}
const cancelChangingName = () => {
  nameForChanging.value = name.value
  nameError.value = ''
}

const signinModalTitle = ref('Вход')

const openSigninModal = (title: string): void => {
  signinModalTitle.value = title
  openModal(RModalSignIn, { title: signinModalTitle })
}

const userMenu = computed(() => {
  return [
    { url: '#', text: user.value?.email ?? '', disabled: true },
    { url: '#', text: 'Выйти', handler: logout },
  ]
})

const menu = computed(() => {
  const items = []
  if (isAdmin.value && type.value === CardType.ProductCard) {
    items.push({ url: '#', text: 'Добавить в шаблоны', handler: addToTemplates })
  }
  if (isAdmin.value && type.value === CardType.Template) {
    items.push({ url: '#', text: 'Убрать из шаблонов', handler: removeFromTemplates })
  }
  items.push(
    { url: '#', text: 'Переименовать', handler: openChangeNameModal },
    { url: '#', text: 'Дублировать', handler: cloneCard },
  )
  if (id.value !== null) {
    items.push({ url: '#', text: 'Удалить', handler: openDeleteFileModal })
  }
  return items
})

const addToTemplates = () => {
  if (id.value === null) {
    const add = () => {
      store.addToTemplates()
      $off('editor:saved', add)
    }
    $event('editor:save')
    $listen('editor:saved', add)
  } else {
    store.addToTemplates()
  }
}

const removeFromTemplates = () => {
  store.removeFromTemplates()
}

const cloneCard = () => {
  $event('editor:clone')
}

const cloneTemplate = () => {
  store.setSteps([ getCurrentStep.value ], false)
  useRouter().push('/file')
}

const logout = (): void => {
  authStore.logout()
}

const toCards = (): void => {
  if (isTemplate.value) {
    useRouter().push('/templates')
    return
  }
  if (isAuth.value) {
    useRouter().push('/')
  } else {
    openSigninModal('Вход')
  }
}

const openDeleteFileModal = async () => {
  const modal = await openModal(RModalDeleteFile, {
    name: name,
  })
  modal.on('delete', deleteFile)
}

const markDownload = () => {
  track('Image downloaded')
  authStore.markDownload().then((result) => {
    if (result) {
      $event('editor:download')
    }
  })
}

const openEditorNav = () => {
  const items: {
    callback: () => void
    text: string
    showOnTablet?: boolean
  }[] = [
    { callback: openChangeNameModal, text: 'Переименовать' },
    { callback: cloneCard, text: 'Дублировать' },
  ]

  if (store.id !== null) {
    items.push({ callback: openDeleteFileModal, text: 'Удалить' })
  }

  items.push(
    { callback: () => { openModal(RModalSupport) }, text: 'Поддержка', showOnTablet: false },
    { callback: () => { openModal(RModalInstruction) }, text: 'Инструкция', showOnTablet: false },
    { callback: toCards, text: 'Все карточки', showOnTablet: false },
  )
  openModal(RModalMobileNav, {
    title: name,
    items: items,
  })
}

const openHomeNav = () => {
  openModal(RModalMobileNav, {
    title: 'Ещё',
    items: [
      { callback: () => { openModal(RModalSupport) }, text: 'Поддержка' },
      { callback: () => { openModal(RModalInstruction) }, text: 'Инструкция' },
      { callback: () => { closeModal(); useRouter().push('/pricing') }, text: 'Тарифы' },
    ],
  })
}

const text = '<span class="p-4-8">Сохранено <a href="/" class="tooltip-link">в профиле</a><span>'
</script>

<template>
  <div
    :class="{ 'editor': isEditor }"
    class="header"
  >
    <div class="container-fluid">
      <div class="d-flex align-items-center position-relative">
        <div class="header__left">
          <NuxtLink
            :to="isAuth ? '/files' : '/'"
            :class="{ 'd-none d-md-block': isEditor }"
            aria-label="Magvi"
          >
            <AIconLogo
              class="logo me-3"
              :class="{ 'text-light': isEditor }"
            />
          </NuxtLink>
          <ul class="nav">
            <li
              v-if="!isEditor"
              class="nav-item"
            >
              <NuxtLink
                to="/templates"
                class="nav-link px-12"
              >
                Шаблоны
              </NuxtLink>
            </li>
            <li class="nav-item hidden-md">
              <a
                :class="{ 'nav-link--editor': isEditor }"
                class="nav-link px-12"
                href="#"
                @click.prevent="openModal(RModalSupport)"
              >Поддержка</a>
            </li>
            <li class="nav-item hidden-md">
              <a
                :class="{ 'nav-link--editor': isEditor }"
                class="nav-link px-12"
                href="#"
                @click.prevent="openModal(RModalInstruction)"
              >Инструкция</a>
            </li>
            <li
              v-if="!isEditor"
              class="nav-item hidden-md"
            >
              <NuxtLink
                to="/pricing"
                :class="{ 'nav-link--editor': isEditor }"
                class="nav-link px-12"
              >
                Тарифы
              </NuxtLink>
            </li>
            <li
              v-if="!isEditor"
              class="nav-item d-md-none"
            >
              <div
                class="nav-link px-12"
                @click="openHomeNav"
              >
                <span>Ещё</span> 
                <span class="ms-1">
                  <BootstrapIconCaretDownFill class="icon-size-10" />
                </span>
              </div>
            </li>
            <li
              v-if="isEditor"
              class="nav-item"
            >
              <button
                class="btn-logo d-md-none"
                @click="openEditorNav"
              >
                <AIconLogo
                  class="logo me-1 text-light"
                />
                <BootstrapIconChevronDown
                  class="text-light icon-size-20"
                />
              </button>  
              <div
                class="nav-link px-12 d-none d-md-block d-lg-none"
                :class="{ 'nav-link--editor': isEditor }"
                @click="openEditorNav"
              >
                <span>Ещё</span> 
                <span class="ms-1">
                  <BootstrapIconCaretDownFill class="icon-size-10" />
                </span>
              </div>
            </li>
          </ul>
        </div>

        <div
          v-if="isEditor"
          class="header__center hidden-lg"
        >
          <template v-if="isTemplate">
            <div class="dropdown d-flex">
              <span
                class="header__center_right dropdown-toggle d-flex"
                type="button"
                ref="cardDropDown"
              >
                <span class="header__center_title">{{ name }}</span>
              </span>
            </div>
          </template>
          <template v-else>
            <div class="d-flex">
              <span
                class="header__center_left"
                @click="toCards"
              >
                Карточки
              </span>
              <span class="header__center_slash">&nbsp;/&nbsp;</span>
            </div>
            <div class="dropdown d-flex">
              <FDropdown
                :distance="21"
                :delay="0.3"
              >
                <span
                  class="header__center_right hover dropdown-toggle d-flex"
                  type="button"
                  ref="cardDropDown"
                >
                  <span class="header__center_title">{{ name }}</span>
                  <span class="ms-1">
                    <BootstrapIconCaretDownFill class="icon-size-10" />
                  </span>
                </span>
                <template #popper="{ hide }">
                  <MDropdownMenu
                    :items="menu"
                    :custom-arrow="true"
                    theme="dark"
                    @click="hide"
                  />
                </template>
              </FDropdown>
            </div>
          </template>
        </div>

        <div class="header__right">
          <div
            v-if="!isEditor"
            class="d-flex align-items-center"
          >
            <iframe
              v-if="!isPaid"
              src="https://yandex.ru/sprav/widget/rating-badge/176658118910?type=rating"
              width="150"
              height="50"
              frameborder="0"
              class="d-none d-md-block mr-12"
              title="yandex rate"
            />
            <div v-if="!isAuth">
              <AButton
                :button-style="ButtonStyle.OutlinePrimary"
                class="d-none d-md-block"
                @click="openSigninModal('Вход')"
              >
                Войти
              </AButton>
              <button
                class="d-md-none btn-login color-primary"
                @click="openSigninModal('Вход')"
                aria-label="Вход"
              >
                <BootstrapIconBoxArrowInRight class="icon-size-24" />
              </button>
            </div>
            <div
              v-else
              class="dropdown"
            >
              <FDropdown
                placement="bottom-end"
                :distance="10"
                :delay="0.3"
              >
                <button
                  class="btn-avatar dropdown-toggle"
                  type="button"
                  ref="userDropDown"
                >
                  <BootstrapIconPerson class="icon-size-20" />
                </button>
                <template #popper="{ hide }">
                  <MDropdownMenu
                    :items="userMenu"
                    position="end"
                    @click="hide"
                  />
                </template>
              </FDropdown>
            </div>
          </div>

          <div v-else>
            <div v-if="!isTemplate">
              <AButton
                v-if="!isAuth"
                :button-style="ButtonStyle.OutlineLight"
                :disabled="isInfograficState"
                class="me-2 prevent_unselection"
                type="button"
                @click="openSigninModal('Войдите, чтобы скачать')"
              >
                Скачать 3/3
              </AButton>
              <AButton
                v-else-if="isPaid"
                :button-style="ButtonStyle.OutlineLight"
                :disabled="isInfograficState"
                class="me-2 prevent_unselection"
                type="button"
                @click="markDownload"
              >
                Скачать
              </AButton>
              <AButton
                v-else-if="user?.remaining_downloads !== 0"
                :button-style="ButtonStyle.OutlineLight"
                :disabled="isInfograficState"
                class="me-2 prevent_unselection"
                type="button"
                @click="markDownload"
              >
                Скачать {{ user?.remaining_downloads ?? 3 }}/3
              </AButton>
              <template v-else>
                <AButton
                  :button-style="ButtonStyle.OutlineLight"
                  :disabled="isInfograficState"
                  class="me-2 prevent_unselection"
                  type="button"
                  @click="openModal(RModalDownloadNotAvailable)"
                >
                  Скачать 0/3
                </AButton>
              </template>
              <AButton
                v-if="!isAuth"
                :button-style="ButtonStyle.Primary"
                :disabled="isInfograficState"
                class="save-button prevent_unselection"
                @click="openSigninModal('Войдите, чтобы сохранить')"
              >
                Сохранить
              </AButton>
              <ClientOnly v-else>
                <AButton
                  :button-style="ButtonStyle.Primary"
                  :disabled="isInfograficState"
                  class="save-button prevent_unselection"
                  @click="saveActualData"
                  v-tooltip="{
                    content: text,
                    html: true,
                    shown: saved,
                    triggers: [],
                    distance: 24,
                  }"
                >
                  <template v-if="!saved">
                    Сохранить
                  </template>
                  <BootstrapIconCheckCircle
                    v-else
                    class="icon-check"
                  />
                </AButton>
              </ClientOnly>
            </div>
            <div v-else>
              <AButton
                :button-style="ButtonStyle.Primary"
                @click="cloneTemplate"
              >
                Дублировать
              </AButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div
    v-if="popupType"
    class="alert-wrap d-flex justify-content-center prevent_unselection"
  >
    <MAlert
      class="justify-content-between w-fit-content"
      :type="popupType"
      @close="closePopup"
    >
      <template #text>
        <div class="me-2">
          {{ popupText }}
        </div>
      </template>
    </MAlert>
  </div>
  <RModalContainer />
</template>
